import React, { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { formatIdDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const COLOR_LABELS = { black: "Hitam", navy: "Biru Navy", brown: "Coklat" };
const ROLE_PRESETS = ["Kepala Gudang", "Admin Kantor", "Supir", "Pelanggan", "Petugas Loading", "Security"];

// Match the backend `_pick_dibuat_sig` / `_pick_diterima_sig` logic so the
// UI shows the SAME mapping decision the PDF renderer will make. Kept as
// pure helpers so they're trivially unit-testable if we ever add jest.
const DIBUAT_KEYWORDS = ["pembuat spb", "admin", "kepala gudang", "petugas loading"];
const DITERIMA_KEYWORDS = ["supir", "pelanggan"];

function _matchAny(sig, keywords) {
  const blob = `${sig?.role_label || ""} ${sig?.signer_role || ""}`.toLowerCase();
  return keywords.some((kw) => blob.includes(kw));
}

function computePdfBoxMap(sigs) {
  // Returns { [nonce]: "DIBUAT" | "DITERIMA" | null }
  if (!sigs || sigs.length === 0) return {};
  const byTime = [...sigs].sort((a, b) => (a.signed_at || "").localeCompare(b.signed_at || ""));
  // DIBUAT: first explicit match by latest signed_at, else earliest overall.
  const dibuatMatches = sigs.filter((s) => _matchAny(s, DIBUAT_KEYWORDS));
  let dibuat = dibuatMatches.length
    ? [...dibuatMatches].sort((a, b) => (b.signed_at || "").localeCompare(a.signed_at || ""))[0]
    : byTime[0];
  // DITERIMA: explicit supir/pelanggan match by latest, else latest non-DIBUAT sig.
  const diterimaMatches = sigs.filter((s) => _matchAny(s, DITERIMA_KEYWORDS));
  let diterima;
  if (diterimaMatches.length) {
    diterima = [...diterimaMatches].sort((a, b) => (b.signed_at || "").localeCompare(a.signed_at || ""))[0];
  } else {
    const others = sigs.filter((s) => s.nonce !== dibuat?.nonce);
    diterima = others.length
      ? [...others].sort((a, b) => (b.signed_at || "").localeCompare(a.signed_at || ""))[0]
      : null;
  }
  const map = {};
  if (dibuat) map[dibuat.nonce] = "DIBUAT";
  if (diterima) map[diterima.nonce] = "DITERIMA";
  return map;
}

const BOX_BADGE_STYLES = {
  DIBUAT: "bg-emerald-100 text-emerald-800 border-emerald-400",
  DITERIMA: "bg-sky-100 text-sky-800 border-sky-400",
};

// Self-contained QR signatures section for SPBDetail:
//   - Header + "Tandatangani (QR)" button
//   - Role picker dialog with a live color-aware preview QR
//   - Grid of already-recorded signatures with per-signer QR thumbnails
export default function QRSignaturesPanel({ spb, spbId, signing, onSign, onOpenPicker }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [customLabel, setCustomLabel] = useState("");
  const [qrColor, setQrColor] = useState("black");

  // Refresh saved QR color whenever the signing dialog is opened so the
  // preview always reflects the latest Layout Editor setting.
  useEffect(() => {
    if (!pickerOpen) return undefined;
    let cancelled = false;
    api.get("/settings/qr")
      .then((r) => { if (!cancelled) setQrColor(r.data?.color || "black"); })
      .catch(() => {});
    return () => { cancelled = true; };
    // `api` is a module-scope axios instance; `setQrColor` is a stable
    // setter. Only `pickerOpen` is a reactive value worth tracking.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerOpen]);

  const submit = (label) => {
    setPickerOpen(false);
    setCustomLabel("");
    onSign((label || "").trim());
  };

  const openPicker = () => {
    setPickerOpen(true);
    if (onOpenPicker) onOpenPicker();
  };

  const signatures = spb.qr_signatures || [];
  const boxMap = computePdfBoxMap(signatures);
  return (
    <div className="border border-border bg-card mb-8" data-testid="qr-signatures-panel">
      <div className="p-4 border-b border-border flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div>
            <div className="overline">Tanda Tangan (QR)</div>
            <div className="text-sm font-semibold">
              {signatures.length} tanda tangan tercatat
            </div>
          </div>
        </div>
        <Button
          onClick={openPicker}
          disabled={signing}
          className="rounded-lg font-bold uppercase tracking-wider no-print"
          data-testid="qr-sign-button"
        >
          {signing ? "Menandatangani…" : "+ Tandatangani (QR)"}
        </Button>
      </div>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="rounded-lg max-w-md" data-testid="qr-role-picker">
          <DialogHeader>
            <DialogTitle className="uppercase tracking-wider text-sm">Pilih Peran / Jabatan</DialogTitle>
          </DialogHeader>
          {/* Static preview so signers know what the resulting QR will look like. */}
          <div
            className="flex items-center gap-3 border border-border bg-secondary/40 p-3 mt-1"
            data-testid="qr-sign-preview"
          >
            <img
              src={`${API_BASE}/public/signature/qr-preview.png?color=${qrColor}`}
              alt="Preview QR"
              className="w-20 h-20 border border-border bg-white shrink-0"
              data-testid="qr-sign-preview-img"
            />
            <div className="text-[11px] leading-snug">
              <div className="overline mb-1">Preview QR</div>
              <div>
                Warna: <span className="font-bold uppercase tracking-wider" data-testid="qr-sign-preview-color">
                  {COLOR_LABELS[qrColor] || qrColor}
                </span>
              </div>
              <div className="text-muted-foreground mt-0.5">
                Ubah warna di <em>Layout Editor</em> jika perlu.
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {ROLE_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => submit(preset)}
                className="border-2 border-border hover:bg-primary hover:text-primary-foreground hover:border-primary p-3 text-sm font-bold uppercase tracking-wider transition-colors"
                data-testid={`qr-preset-${preset.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {preset}
              </button>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-border">
            <Label className="overline text-xs mb-2 block">Atau ketik manual:</Label>
            <div className="flex gap-2">
              <Input
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                placeholder="cth: Manajer Operasional"
                className="rounded-lg flex-1"
                data-testid="qr-custom-label-input"
                onKeyDown={(e) => { if (e.key === "Enter") submit(customLabel); }}
              />
              <Button
                type="button"
                onClick={() => submit(customLabel)}
                className="rounded-lg"
                data-testid="qr-custom-label-submit"
              >
                Tandatangani
              </Button>
            </div>
            <button
              type="button"
              onClick={() => submit("")}
              className="w-full mt-3 text-xs text-muted-foreground hover:text-foreground underline"
              data-testid="qr-sign-no-label"
            >
              Skip jabatan — Tandatangani tanpa label
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {signatures.length > 0 ? (
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {signatures.map((s, i) => {
            const box = boxMap[s.nonce] || null;
            return (
              <div key={`${s.nonce}-${i}`}
                   className="border border-border p-3 flex items-start gap-3"
                   data-testid={`qr-signature-${i}`}>
                <img
                  src={`${API_BASE}/public/signature/qr-by-nonce/${spbId}/${s.nonce}.png?v=2`}
                  alt={`QR tanda tangan ${s.signer_name}`}
                  className="w-24 h-24 shrink-0 border border-border bg-white"
                />
                <div className="text-xs font-mono space-y-1 flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-sm truncate">{s.signer_name || s.signer}</span>
                    {box ? (
                      <span
                        className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 border ${BOX_BADGE_STYLES[box]}`}
                        data-testid={`qr-sig-box-badge-${i}`}
                        title={`QR ini akan dicetak di kotak ${box} OLEH pada PDF`}
                      >
                        → {box}
                      </span>
                    ) : (
                      <span
                        className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 border bg-muted text-muted-foreground border-border"
                        data-testid={`qr-sig-box-badge-${i}`}
                        title="Signature ini hanya jadi arsip verifikasi — tidak dicetak di kotak DIBUAT/DITERIMA"
                      >
                        arsip
                      </span>
                    )}
                  </div>
                  <div className="text-muted-foreground truncate">{s.signer}</div>
                  <div className="uppercase text-[10px] tracking-wider">
                    {s.role_label || s.signer_role || <span className="text-muted-foreground italic">(tanpa label)</span>}
                  </div>
                  <div className="text-muted-foreground">
                    {formatIdDateTime(s.signed_at)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="p-6 text-sm text-muted-foreground text-center no-print">
          Belum ada tanda tangan. Klik <strong>Tandatangani (QR)</strong> untuk menandatangani SPB ini.
          QR yang dihasilkan bisa dipindai untuk verifikasi.
        </div>
      )}
    </div>
  );
}
